import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog } from 'radix-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ABNORMAL_FLAGS,
  DEMO_LAB_TESTS,
  type AbnormalFlag,
  type LabResultResponse,
} from '@emr/shared';

import { createLabResult } from '../../../api/chart.ts';
import { errorMessage, isContractMismatch } from '../../../api/client.ts';
import { FailureNotice } from '../../../components/AsyncState.tsx';
import { Button, ErrorText, Field, SelectField } from '../../../components/primitives.tsx';
import { fromDatetimeLocalValue } from '../../../schedule/datetimeLocalInput.ts';

export interface AddLabResultDialogProps {
  readonly patientId: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSaved: (labResult: LabResultResponse) => void;
}

interface FormState {
  readonly testName: string;
  readonly value: string;
  readonly abnormalFlag: AbnormalFlag;
  readonly resultedAt: string; // datetime-local value
}

const FIRST_TEST = DEMO_LAB_TESTS[0];
if (FIRST_TEST === undefined) throw new Error('DEMO_LAB_TESTS must not be empty');

const EMPTY_FORM: FormState = {
  testName: FIRST_TEST.name,
  value: '',
  abnormalFlag: 'normal',
  resultedAt: '',
};

/**
 * `LabsTab.tsx`'s own create form. Modeled on `AddAllergyDialog.tsx`'s shape (Radix `Dialog`, one
 * `useMutation`, the same `isContractMismatch`/`FailureNotice` split), with a closed test/flag choice
 * (`SelectField`, matching `createLabResultRequestSchema`'s own enum-validated fields) instead of
 * free text, and a `datetime-local` input for `resultedAt` reusing `schedule/datetimeLocalInput.ts`'s
 * conversion rather than reimplementing it — this project's own "favor the general, reusable
 * solution once a pattern actually recurs" rule, since `AppointmentFormDialog.tsx` already solved the
 * exact same local-value-to-`Z`-suffixed-instant conversion.
 */
export function AddLabResultDialog({
  patientId,
  open,
  onOpenChange,
  onSaved,
}: AddLabResultDialogProps) {
  const { t } = useTranslation('patientChartTabs');
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  // Re-seed on every open, not just once — matching `AddAllergyDialog.tsx`'s own re-seed guard, same
  // reasoning: closing after a save and reopening should show a blank form, not the last submission.
  const [seededForOpen, setSeededForOpen] = useState(false);
  if (!open && seededForOpen) {
    setSeededForOpen(false);
  } else if (open && !seededForOpen) {
    setForm(EMPTY_FORM);
    setFormError(null);
    setSeededForOpen(true);
  }

  const create = useMutation({
    mutationFn: () => {
      const resultedAt = fromDatetimeLocalValue(form.resultedAt);
      if (resultedAt === null)
        throw new Error('resultedAt was validated before mutate() was called');
      return createLabResult(patientId, {
        encounterId: null,
        testName: form.testName,
        value: form.value.trim(),
        abnormalFlag: form.abnormalFlag,
        resultedAt,
        confidentialityReasons: [],
      });
    },
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['patient', patientId, 'labs'] });
      onSaved(created);
      onOpenChange(false);
    },
  });

  function handleSubmit() {
    setFormError(null);
    if (form.value.trim().length === 0) {
      setFormError(t('addLabResultDialog.valueRequired'));
      return;
    }
    if (fromDatetimeLocalValue(form.resultedAt) === null) {
      setFormError(t('addLabResultDialog.resultedAtRequired'));
      return;
    }
    create.mutate();
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 z-50 flex max-h-[90vh] w-[min(28rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 overflow-y-auto rounded-panel border border-line bg-surface p-4 shadow-overlay sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <Dialog.Title className="m-0 text-lg font-semibold text-ink">
              {t('addLabResultDialog.title')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <Button variant="secondary" aria-label={t('addLabResultDialog.close')}>
                {t('addLabResultDialog.close')}
              </Button>
            </Dialog.Close>
          </div>
          <Dialog.Description className="sr-only">
            {t('addLabResultDialog.description')}
          </Dialog.Description>

          <SelectField
            label={t('addLabResultDialog.testLabel')}
            value={form.testName}
            onChange={(event) => {
              setForm((current) => ({ ...current, testName: event.target.value }));
            }}
          >
            {DEMO_LAB_TESTS.map((test) => (
              <option key={test.name} value={test.name}>
                {test.name} ({test.unit}, {test.referenceRange})
              </option>
            ))}
          </SelectField>

          <Field
            label={t('addLabResultDialog.valueLabel')}
            value={form.value}
            onChange={(event) => {
              setForm((current) => ({ ...current, value: event.target.value }));
            }}
            maxLength={20}
            placeholder={t('addLabResultDialog.valuePlaceholder')}
            required
          />

          <SelectField
            label={t('addLabResultDialog.flagLabel')}
            value={form.abnormalFlag}
            onChange={(event) => {
              setForm((current) => ({
                ...current,
                abnormalFlag: event.target.value as AbnormalFlag,
              }));
            }}
          >
            {ABNORMAL_FLAGS.map((flag) => (
              <option key={flag} value={flag}>
                {t(`addLabResultDialog.flags.${flag}`)}
              </option>
            ))}
          </SelectField>

          <Field
            label={t('addLabResultDialog.resultedAtLabel')}
            type="datetime-local"
            value={form.resultedAt}
            onChange={(event) => {
              setForm((current) => ({ ...current, resultedAt: event.target.value }));
            }}
            required
          />

          {formError !== null ? <ErrorText message={formError} /> : null}
          {create.error !== null ? (
            isContractMismatch(create.error) ? (
              <FailureNotice error={create.error} />
            ) : (
              <ErrorText message={errorMessage(create.error)} />
            )
          ) : null}

          <div className="flex justify-end gap-3">
            <Button pending={create.isPending} onClick={handleSubmit}>
              {create.isPending
                ? t('addLabResultDialog.submitting')
                : t('addLabResultDialog.submit')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
